import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';
import { useSeriesDailyClaim } from 'hooks/packs/useSeriesDailyClaim';

import type { SeriesDailyClaimModule_task } from './__generated__/index.graphql';

const Wrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--double-unit);
`;

const glowCircleAnimation = keyframes`
  0% {
    box-shadow:
    0px 0px 20px 0px rgba(0, 243, 235, 0.6),
    inset 0px 0px 32px 0px rgba(0, 243, 235, 0.3),
    inset 0px 0px 8px 0px rgba(0, 243, 235, 0.4),
    inset 0px 0px 4px 1px rgba(0, 243, 235, 0.8);
  }
  50% {

    box-shadow:
      0px 0px 24px 0px rgba(0, 243, 235, 0.6),
      inset 0px 0px 36px 0px rgba(0, 243, 235, 0.3),
      inset 0px 0px 10px 0px rgba(0, 243, 235, 0.4),
      inset 0px 0px 6px 1px rgba(0, 243, 235, 0.8),
      0px 0px 20px 0px rgba(0, 243, 235, 0.6),
      inset 0px 0px 32px 0px rgba(0, 243, 235, 0.3),
      inset 0px 0px 8px 0px rgba(0, 243, 235, 0.4),
      inset 0px 0px 4px 1px rgba(0, 243, 235, 0.8);
  }
  100% {
    box-shadow:
      0px 0px 20px 0px rgba(0, 243, 235, 0.6),
      inset 0px 0px 32px 0px rgba(0, 243, 235, 0.3),
      inset 0px 0px 8px 0px rgba(0, 243, 235, 0.4),
      inset 0px 0px 4px 1px rgba(0, 243, 235, 0.8);
  }
`;

const GlowCircle = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: rgba(14, 14, 14, 0.54);
  backdrop-filter: blur(20px);
  box-shadow:
    0px 0px 20px 0px rgba(0, 243, 235, 0.6),
    inset 0px 0px 32px 0px rgba(0, 243, 235, 0.3),
    inset 0px 0px 8px 0px rgba(0, 243, 235, 0.4),
    inset 0px 0px 4px 1px rgba(0, 243, 235, 0.8);
  position: relative;
  .disabled & {
    box-shadow: 0px 0px 0px 2px var(--c-nd-300);
    background: transparent;
  }
  .loading & {
    animation: ${glowCircleAnimation} 2.5s ease-in-out infinite;
  }
`;

const Positionned = styled.div`
  position: absolute;
  left: 50%;
  top: 55%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const PackImage = styled.img`
  width: 32px;
  height: 48px;
  object-fit: contain;
  transition: opacity 0.3s ease-in-out;
  .loading & {
    opacity: 0.6;
  }
`;

const FreeBadge = styled(Chip)`
  margin-top: calc(-1 * var(--unit));
`;

const NotificationDot = styled.div`
  position: absolute;
  right: calc(-1 * var(--unit));
  top: var(--half-unit);
`;

type Props = {
  task?: SeriesDailyClaimModule_task;
  refetch: () => Promise<unknown>;
  onClaimed?: () => void;
};

export const SeriesDailyClaimModule = ({ task, refetch, onClaimed }: Props) => {
  const {
    loading,
    isClaimable,
    pack,
    onClaim,
    reward,
    packDialogOpen,
    setPackDialogOpen,
    claimMutationData,
    dailyTasksExpiresAt,
  } = useSeriesDailyClaim(task, onClaimed);

  return (
    <>
      <Wrapper
        onClick={onClaim}
        disabled={!isClaimable}
        className={classNames({ disabled: !isClaimable, loading })}
      >
        {!!loading && (
          <Positionned>
            <LoadingIndicator smaller />
          </Positionned>
        )}
        <GlowCircle>
          {!!pack?.pictureUrl && <PackImage src={pack.pictureUrl} alt="" />}
        </GlowCircle>
        {!!isClaimable && (
          <NotificationDot>
            <LiveDot animate size="md" />
          </NotificationDot>
        )}
        <FreeBadge>
          <LabelS bold>
            {isClaimable ? (
              <FormattedMessage
                id="FreePackButton.free"
                defaultMessage="Free"
              />
            ) : (
              dailyTasksExpiresAt && (
                <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
              )
            )}
          </LabelS>
        </FreeBadge>
      </Wrapper>
      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

SeriesDailyClaimModule.fragments = {
  task: gql`
    fragment SeriesDailyClaimModule_task on Task {
      id
      ...useSeriesDailyClaim_task
    }
    ${useSeriesDailyClaim.fragments.task}
  ` as TypedDocumentNode<SeriesDailyClaimModule_task>,
};
